import React, { useState } from 'react';
import { Database, Cpu, Globe, CheckCircle, ArrowDown, Search, Zap, Shield, AlertCircle, DollarSign, FileText, LucideIcon } from 'lucide-react';

type Props = {
  theme: 'light' | 'dark';
};

type Layer = {
  id: string;
  name: string;
  color: string;
  icon: LucideIcon;
  tech: string[];
  description: string;
  responsibilities: string[];
  mvpNote: string;
  isNew?: boolean;
  isCanon?: boolean;
};

const SiloettArchitecture: React.FC<Props> = ({ theme }) => {
  const [activeLayer, setActiveLayer] = useState<string | null>(null);
  const [showDataFlow, setShowDataFlow] = useState(false);
  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-app-dark text-white' : 'bg-app-light text-slate-900';
  const cardBg = isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-slate-200 text-slate-900';
  const muted = isDark ? 'text-gray-300' : 'text-slate-600';
  const subMuted = isDark ? 'text-gray-200' : 'text-slate-700';

  const layers: Layer[] = [
    {
      id: 'layer0',
      name: 'Layer 0: AI-DRM Knowledge Store (NEW)',
      color: 'from-red-500 to-red-600',
      icon: Shield,
      tech: ['Pinecone (Contracts)', 'PostgreSQL (Rights DB)', 'Excel Ingestion'],
      description: 'Rights contracts, distribution deals, market performance data',
      responsibilities: [
        'Contract embeddings (territory, exclusivity, terms)',
        'Rights metadata (availability windows, pricing)',
        'Performance analytics (viewership, revenue by territory)',
        'Deal structure parsing (revenue splits, minimums)'
      ],
      isNew: true,
      mvpNote: 'NEW MVP LAYER - Contract ingestion and rights tracking'
    },
    {
      id: 'layer5',
      name: 'Layer 5: User Interface',
      color: 'from-blue-500 to-blue-600',
      icon: Globe,
      tech: ['React (Vite/CRA)', 'Frontend Hosting (Vercel / Netlify / CloudFlare)'],
      description: 'Dual interface: Rights Management + Canon Management',
      responsibilities: [
        'Rights availability queries',
        'Revenue optimization dashboard',
        'Canon search & validation',
        'Contract compliance alerts'
      ],
      mvpNote: 'Simplified UI for core workflows'
    },
    {
      id: 'layer4',
      name: 'Layer 4: Application Logic',
      color: 'from-purple-500 to-purple-600',
      icon: Cpu,
      tech: ['FastAPI (Python)', 'Backend Hosting (Railway / Render / AWS)'],
      description: 'API orchestration for both rights and canon workflows',
      responsibilities: [
        'Rights query routing',
        'Canon validation routing',
        'Cross-layer data synthesis',
        'Session & auth management'
      ],
      mvpNote: 'Manual ingestion approval process'
    },
    {
      id: 'layer3',
      name: 'Layer 3: Intelligence Engines',
      color: 'from-green-500 to-green-600',
      icon: Zap,
      tech: ['LangChain Framework', 'LLM Provider (Claude / GPT-4 / Azure OpenAI)'],
      description: 'Parallel RAG pipelines for rights AND canon',
      responsibilities: [
        'Rights RAG (contracts + market data)',
        'Canon RAG (scripts + bibles)',
        'Opportunity detection algorithms',
        'Conflict validation (rights + story)'
      ],
      mvpNote: 'Core RAG functionality complete'
    },
    {
      id: 'layer2',
      name: 'Layer 2: Canon Knowledge Store',
      color: 'from-orange-500 to-orange-600',
      icon: Database,
      tech: ['Vector DB (Pinecone / pgvector / Weaviate)', 'PostgreSQL (Metadata & Relations)'],
      description: 'Story canon, character data, timelines',
      responsibilities: [
        'Script embeddings',
        'Character/event metadata',
        'Timeline consistency data'
      ],
      isCanon: true,
      mvpNote: 'Fully functional; rule authoring partially manual'
    }
  ];

  const dataFlowSteps = [
    { from: 'Producer', to: 'UI', label: 'Query: "MasterChef available in Japan?"', color: 'blue', bgColor: '#3b82f6' },
    { from: 'UI', to: 'API', label: 'REST call with show ID + territory', color: 'purple', bgColor: '#8b5cf6' },
    { from: 'API', to: 'DRM RAG', label: 'Trigger rights retrieval', color: 'green', bgColor: '#22c55e' },
    { from: 'DRM RAG', to: 'DRM DB', label: 'Search contract embeddings', color: 'red', bgColor: '#ef4444' },
    { from: 'DRM DB', to: 'DRM RAG', label: 'Return: Netflix exclusive until 2027', color: 'red', bgColor: '#ef4444' },
    { from: 'DRM RAG', to: 'API', label: 'Rights analysis + alternatives', color: 'green', bgColor: '#22c55e' },
    { from: 'API', to: 'UI', label: 'Display conflict + suggest YouTube', color: 'purple', bgColor: '#8b5cf6' },
    { from: 'UI', to: 'Producer', label: 'Show results with citations', color: 'blue', bgColor: '#3b82f6' }
  ];

  return (
    <div className={`min-h-screen ${pageBg} p-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>SILOETT Technical Architecture</h1>
          <p className={`${muted} text-lg`}>AI-Powered Rights Management + Canon Operating System</p>
          <div className="flex gap-4 justify-center mt-6">
            <button
              onClick={() => setShowDataFlow(!showDataFlow)}
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-orange-500 hover:scale-102 text-white rounded-lg transition-all"
            >
              {showDataFlow ? 'Hide' : 'Show'} Data Flow
            </button>
            <button
              onClick={() => setActiveLayer(null)}
              className={`px-6 py-2 hover:scale-102 rounded-lg transition-all border ${isDark ? 'bg-gray-800 text-gray-200 border-gray-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}
            >
              Reset View
            </button>
          </div>
        </div>

        {/* NEW LAYER CALLOUT */}
        <div className={`mb-12 rounded-xl p-6 border-2 ${isDark ? 'bg-gradient-to-r from-red-900 to-red-800 border-red-500' : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-400'}`}>
          <div className="flex items-center gap-4">
            <Shield className={`w-12 h-12 ${isDark ? 'text-white' : 'text-red-600'}`} />
            <div>
              <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>NEW: Layer 0 - AI-DRM Manager</h3>
              <p className={`${isDark ? 'text-white text-opacity-90' : 'text-slate-700'} mt-2`}>
                Foundation layer that ingests distribution contracts, tracks rights availability, 
                and identifies untapped revenue opportunities across your content library.
              </p>
            </div>
          </div>
        </div>

        {/* Architecture Layers */}
        <div className="space-y-6 mb-12">
          {layers.map((layer, index) => {
            const Icon = layer.icon;
            const isActive = activeLayer === layer.id;

            return (
              <div key={layer.id}>
                <div
                  onClick={() => setActiveLayer(isActive ? null : layer.id)}
                  className={`
                    cursor-pointer transition-all duration-300 rounded-xl p-6 
                    ${isActive ? 'scale-105 shadow-2xl' : 'hover:scale-102 shadow-lg'}
                    ${layer.isCanon ? 'p-8 border-4 border-orange-500 border-opacity-30' : ''}
                    ${layer.isNew ? 'ring-4 ring-yellow-400 ring-opacity-50' : ''}
                    ${cardBg}
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg border ${
                      layer.isNew 
                        ? `bg-red-500 bg-opacity-10 border-red-500 border-opacity-30`
                        : `bg-teal-500 bg-opacity-10 border-teal-500 border-opacity-30`
                    }`}>
                      <Icon className={`w-8 h-8 ${
                        layer.isNew
                          ? isDark ? 'text-red-300' : 'text-red-600'
                          : isDark ? 'text-teal-300' : 'text-teal-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{layer.name}</h3>
                        {layer.isNew && (
                          <span className={`px-3 py-1 bg-yellow-400 ${isDark ? 'text-gray-900' : 'text-gray-900'} text-xs font-bold rounded-full`}>
                            NEW MVP LAYER
                          </span>
                        )}
                        {layer.isCanon && (
                          <span className={`px-3 py-1 bg-orange-500 bg-opacity-20 text-orange-500 text-xs font-bold rounded-full`}>
                            MOAT
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {layer.tech.map((tech, i) => (
                          <span key={i} className={`px-3 py-1 bg-teal-500 bg-opacity-10 border border-teal-500 border-opacity-30 rounded-full text-sm ${isDark ? 'text-teal-300' : 'text-teal-600'}`}>
                            {tech}
                          </span>
                        ))}
                      </div>
                      <p className={subMuted}>{layer.description}</p>

                      {isActive && (
                        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                          <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Key Responsibilities:</h4>
                          <ul className="space-y-1 mb-4">
                            {layer.responsibilities.map((resp, i) => (
                              <li key={i} className={`${subMuted} flex items-center gap-2`}>
                                <CheckCircle className={`w-4 h-4 ${isDark ? 'text-teal-300' : 'text-teal-600'}`} />
                                {resp}
                              </li>
                            ))}
                          </ul>
                          <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                            <div className="flex items-start gap-2">
                              <AlertCircle className={`w-4 h-4 mt-0.5 ${isDark ? 'text-orange-300' : 'text-orange-500'}`} />
                              <div>
                                <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-orange-300' : 'text-orange-500'}`}>MVP Status:</p>
                                <p className={`text-sm ${subMuted}`}>{layer.mvpNote}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Arrow between layers */}
                {index < layers.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowDown className={`w-6 h-6 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Data Flow Visualization */}
        {showDataFlow && (
          <div className={`rounded-xl p-8 border shadow-lg ${cardBg}`}>
            <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Search className={`w-6 h-6 ${isDark ? 'text-teal-300' : 'text-teal-600'}`} />
              Example: Rights Availability Query Flow
            </h2>
            <div className="space-y-3">
              {dataFlowSteps.map((step, index) => (
                <div key={index} className="flex items-center gap-4" >
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: step.bgColor }}
                  ></div>
                  <div
                    className={`flex-1 rounded-lg p-4 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-slate-50 border-slate-200'}`}
                    style={{ borderLeft: `4px solid ${step.bgColor}` }}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`${muted} font-mono text-sm`}>{index + 1}.</span>
                      <span className={`${isDark ? 'text-white' : 'text-slate-900'} font-semibold`}>{step.from}</span>
                      <ArrowDown className={`w-4 h-4 rotate-[-90deg] ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                      <span className={`${isDark ? 'text-white' : 'text-slate-900'} font-semibold`}>{step.to}</span>
                    </div>
                    <p className={`${subMuted} text-sm mt-2 ml-8`}>{step.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Example Query */}
            <div className={`mt-8 rounded-lg p-6 border border-red-500 border-opacity-30 ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}>
              <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-red-400' : 'text-red-600'}`}>Example Rights Query:</h3>
              <div className="space-y-2 text-sm">
                <p className={subMuted}><span className="text-blue-400 font-mono">Producer Input:</span> "Is MasterChef Season 5 available in Japan?"</p>
                <p className={subMuted}><span className="text-red-400 font-mono">Contract Search:</span> Retrieves Netflix JP exclusive deal (expires Dec 2027)</p>
                <p className={subMuted}><span className={`${isDark ? 'text-teal-300' : 'text-teal-600'} font-mono`}>AI Analysis:</span> "No - Netflix exclusive. Try YouTube FAST after 2027 or TikTok clips now."</p>
                <p className={subMuted}><span className="text-purple-400 font-mono">Citation:</span> (Source: Netflix JP Agreement 2024, Clause 3.2, Territorial Exclusivity)</p>
              </div>
            </div>
          </div>
        )}

        {/* Dual Advantage Grid */}
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className={`rounded-xl p-6 border shadow-lg ${isDark ? 'bg-gradient-to-br from-red-900 to-red-800 border-red-600' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-400'}`}>
            <Shield className={`w-8 h-8 ${isDark ? 'text-red-300' : 'text-red-600'} mb-3`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>NEW: AI-DRM Manager</h3>
            <ul className={`${isDark ? 'text-gray-300' : 'text-slate-700'} text-sm space-y-1`}>
              <li>✅ Ingest contracts (PDF/Excel)</li>
              <li>✅ Track territory availability</li>
              <li>✅ Detect revenue opportunities</li>
              <li>✅ Validate distribution plans</li>
            </ul>
          </div>
          <div className={`rounded-xl p-6 border shadow-lg ${isDark ? 'bg-gradient-to-br from-orange-900 to-orange-800 border-orange-600' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400'}`}>
            <FileText className={`w-8 h-8 ${isDark ? 'text-orange-300' : 'text-orange-600'} mb-3`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Original: Canon Manager</h3>
            <ul className={`${isDark ? 'text-gray-300' : 'text-slate-700'} text-sm space-y-1`}>
              <li>✅ Ingest scripts/bibles</li>
              <li>✅ Query story canon</li>
              <li>✅ Validate new content</li>
              <li>✅ Generate canon-grounded scripts</li>
            </ul>
          </div>
        </div>

        {/* Combined Value Prop */}
        <div className={`mt-8 rounded-xl p-8 border shadow-lg ${isDark ? 'bg-gradient-to-r from-purple-900 to-blue-900 border-purple-500' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-400'}`}>
          <DollarSign className={`w-10 h-10 ${isDark ? 'text-yellow-400' : 'text-yellow-600'} mb-4`} />
          <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>The SILOETT Advantage</h3>
          <p className={`${isDark ? 'text-gray-200' : 'text-slate-700'} text-lg leading-relaxed`}>
            <strong className={isDark ? 'text-yellow-400' : 'text-yellow-600'}>Before:</strong> Studios manage rights in Excel, canon in Google Docs, and leave millions on the table.
            <br /><br />
            <strong className={isDark ? 'text-yellow-400' : 'text-yellow-600'}>After:</strong> One AI system that maximizes revenue from your library <em>while</em> ensuring new content respects canon. 
            Rights optimization + creative consistency = the only tool transmedia studios need.
          </p>
        </div>

        {/* Technical Advantages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          <div className={`rounded-xl p-6 border shadow-lg ${cardBg}`}>
            <Database className={`w-8 h-8 text-orange-500 mb-3`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Canon as Source of Truth</h3>
            <p className={`${muted} text-sm`}>Canon isn't just context — it's a first-class system that drives all outputs.</p>
          </div>
          <div className={`rounded-xl p-6 border shadow-lg ${cardBg}`}>
            <Shield className="w-8 h-8 text-red-400 mb-3" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Rights + Canon</h3>
            <p className={`${muted} text-sm`}>Dual knowledge stores power both revenue optimization and creative consistency.</p>
          </div>
          <div className={`rounded-xl p-6 border shadow-lg ${cardBg}`}>
            <CheckCircle className={`w-8 h-8 ${isDark ? 'text-teal-300' : 'text-teal-600'} mb-3`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Full Traceability</h3>
            <p className={`${muted} text-sm`}>Every fact includes source citations with document, page, and line numbers.</p>
          </div>
          <div className={`rounded-xl p-6 border shadow-lg ${cardBg}`}>
            <Globe className="w-8 h-8 text-blue-400 mb-3" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Cloud-Agnostic</h3>
            <p className={`${muted} text-sm`}>Architecture supports multiple providers — no vendor lock-in.</p>
          </div>
        </div>

        {/* Strategic Architecture Notes */}
        <div className={`mt-12 rounded-xl p-6 border shadow-lg ${cardBg}`}>
          <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <AlertCircle className={`w-5 h-5 ${isDark ? 'text-teal-300' : 'text-teal-600'}`} />
            Architectural Principles
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className={`${subMuted} mb-2`}>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Cloud-Agnostic Design:</span> All vendor-specific services are abstracted behind interfaces. We can migrate between providers (Pinecone → pgvector, Claude → GPT-4, Railway → AWS) without architectural changes.
              </p>
            </div>
            <div>
              <p className={`${subMuted} mb-2`}>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>MVP Discipline:</span> Core technical architecture is production-ready. Some operational workflows (ingestion approval, rule authoring) are partially manual for MVP — this enables faster validation and iteration.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p className={muted}>Click any layer to see detailed responsibilities and MVP status • Toggle data flow to see query execution</p>
        </div>
      </div>
    </div>
  );
};

export default SiloettArchitecture;

